// Lab Template Editor — full CRUD page at /lab/templates/:id (id = "new" for create).
//
// Layout follows pages/SegmentBuilder: header + Save action top right, two
// columns of panels (metadata + html_body on the left; ContentSelectionConfigForm
// on the right). Save persists via POST or PUT depending on `isNew`.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { api, ApiError } from '../api/client'
import {
  ContentSelectionConfigForm,
  DEFAULT_CONTENT_SELECTION_CONFIG,
  type ContentSelectionConfig,
} from './ContentSelectionConfigForm'

type LabTemplateDetail = {
  id: number
  slug: string
  name: string
  purpose: string
  publication_id: number | null
  subject: string
  preheader: string | null
  html_body: string
  text_body: string | null
  sample_vars: Record<string, unknown>
  variable_defaults: Record<string, unknown>
  content_selection_config: ContentSelectionConfig | null
  enabled: boolean
  created_at: string | null
  updated_at: string | null
}

const PURPOSES = ['renewal', 'welcome', 'winback', 'marketing', 'transactional'] as const
const PUBLICATIONS = [
  { id: 1, slug: 'rc_car_action', name: 'RC Car Action' },
  { id: 2, slug: 'model_airplane_news', name: 'Model Airplane News' },
  { id: 3, slug: 'flight_journal', name: 'Flight Journal' },
  { id: 4, slug: 'diecast', name: 'Die Cast X' },
]

type FormState = {
  slug: string
  name: string
  purpose: string
  publication_id: number | null
  subject: string
  preheader: string
  html_body: string
  text_body: string
  enabled: boolean
}

const BLANK_FORM: FormState = {
  slug: '',
  name: '',
  purpose: 'renewal',
  publication_id: null,
  subject: '',
  preheader: '',
  html_body: `<!-- Inside this template you can iterate over the picked articles
     with the {% raw %}{% for tile in story_tiles %}{% endraw %} block: -->
<div class="tiles">
  {% for tile in story_tiles %}
    <a href="{{ tile.wp_url }}">
      {% if tile.featured_image_url %}
        <img src="{{ tile.featured_image_url }}" alt="" />
      {% endif %}
      <h3>{{ tile.title }}</h3>
      <p>{{ tile.short_teaser }}</p>
    </a>
  {% endfor %}
</div>`,
  text_body: '',
  enabled: true,
}

export function LabTemplateEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new' || !id
  const nav = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<FormState>(BLANK_FORM)
  const [config, setConfig] = useState<ContentSelectionConfig | null>(null)
  const [enableConfig, setEnableConfig] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false)

  const existing = useQuery({
    queryKey: ['lab', 'template', id],
    queryFn: () => api.get<LabTemplateDetail>(`/api/lab/templates/${id}`),
    enabled: !isNew,
    retry: false,
  })

  useEffect(() => {
    if (existing.data) {
      setForm({
        slug: existing.data.slug,
        name: existing.data.name,
        purpose: existing.data.purpose,
        publication_id: existing.data.publication_id,
        subject: existing.data.subject,
        preheader: existing.data.preheader ?? '',
        html_body: existing.data.html_body,
        text_body: existing.data.text_body ?? '',
        enabled: existing.data.enabled,
      })
      if (existing.data.content_selection_config) {
        setConfig({ ...DEFAULT_CONTENT_SELECTION_CONFIG, ...existing.data.content_selection_config })
        setEnableConfig(true)
      } else {
        setConfig(null)
        setEnableConfig(false)
      }
    }
  }, [existing.data])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        preheader: form.preheader || null,
        text_body: form.text_body || null,
        sample_vars: existing.data?.sample_vars ?? {},
        variable_defaults: existing.data?.variable_defaults ?? {},
        content_selection_config: enableConfig
          ? (config ?? DEFAULT_CONTENT_SELECTION_CONFIG)
          : null,
      }
      return isNew
        ? api.post<LabTemplateDetail>('/api/lab/templates', body)
        : api.put<LabTemplateDetail>(`/api/lab/templates/${id}`, body)
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['lab', 'templates'] })
      qc.invalidateQueries({ queryKey: ['lab', 'template', String(tpl.id)] })
      setError(null)
      nav(`/lab/templates/${tpl.id}`, { replace: true })
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        const detail = (e.detail as { detail?: unknown })?.detail ?? e.detail
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
      } else {
        setError(String(e))
      }
    },
  })

  const del = useMutation({
    mutationFn: () => api.del<{ deleted: boolean }>(`/api/lab/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab', 'templates'] })
      nav('/lab/templates', { replace: true })
    },
  })

  const onEnableConfigChange = (next: boolean) => {
    setEnableConfig(next)
    if (next && config === null) setConfig(DEFAULT_CONTENT_SELECTION_CONFIG)
  }

  if (!isNew && existing.error instanceof ApiError && existing.error.status === 404) {
    return (
      <div className="p-8">
        <Link to="/lab/templates" className="label-amber hover:underline">← Lab Templates</Link>
        <div className="mt-6 label text-radar-red">404 — template not found</div>
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/lab/templates" className="label-amber hover:underline">← Lab Templates</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{isNew ? 'New' : form.slug || 'Loading…'}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">{isNew ? 'Composing' : 'Editing'}</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            {form.name || <span className="text-paper-faint italic">Untitled lab template</span>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            confirmDelete ? (
              <>
                <span className="label text-radar-red">Delete this template?</span>
                <Button variant="danger" onClick={() => del.mutate()} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Confirm Delete'}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>Delete</Button>
            )
          )}
          <Button variant="outline" onClick={() => nav('/lab/templates')}>Cancel</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : isNew ? 'Create Template' : 'Update'}
          </Button>
        </div>
      </header>

      {error && (
        <div className="font-mono text-[12px] text-radar-red border border-radar-red/40 bg-radar-red/10 p-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <Panel label="Metadata" accent="amber">
            <div className="grid grid-cols-2 gap-5">
              <Field
                label="Slug"
                value={form.slug}
                onChange={(e) =>
                  setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9_-]/g, '') })
                }
                placeholder="flj_renewal_warmup"
              />
              <Field
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="FLJ Renewal Warm-up"
              />
              <label className="block">
                <span className="label mb-1.5 block">Purpose</span>
                <select
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[12px] text-paper uppercase tracking-label cursor-pointer"
                >
                  {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label mb-1.5 block">Publication</span>
                <select
                  value={form.publication_id ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      publication_id: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  className="w-full bg-ink-700 border border-ink-500 px-3 py-1.5 font-mono text-[12px] text-paper uppercase tracking-label cursor-pointer"
                >
                  <option value="">— multi-title —</option>
                  {PUBLICATIONS.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              <Field
                label="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="A few stories you might have missed, {{first_name}}"
                className="col-span-2"
              />
              <Field
                label="Preheader"
                value={form.preheader}
                onChange={(e) => setForm({ ...form, preheader: e.target.value })}
                placeholder="Optional preview text shown next to the subject"
                className="col-span-2"
              />
              <label className="col-span-2 flex items-start gap-3 cursor-pointer select-none pt-1">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="mt-1"
                />
                <span>
                  <span className="label block">Enabled</span>
                  <span className="font-mono text-[11px] text-paper-trace">
                    Disabled templates can't be selected by campaigns or the dispatcher.
                  </span>
                </span>
              </label>
            </div>
          </Panel>

          <Panel
            label="HTML Body"
            accent="cyan"
            right={
              <span className="font-mono text-[10px] text-paper-trace tracking-label">
                {form.html_body.length.toLocaleString()} chars
              </span>
            }
          >
            <textarea
              value={form.html_body}
              onChange={(e) => setForm({ ...form, html_body: e.target.value })}
              spellCheck={false}
              rows={22}
              className="w-full bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper resize-y leading-snug"
            />
            <div className="mt-2 font-mono text-[11px] text-paper-trace">
              Inside the template, the dispatcher exposes{' '}
              <code className="text-paper">story_tiles</code> as a list of articles
              picked by the selection config below — fields:{' '}
              <code className="text-paper">title</code>,{' '}
              <code className="text-paper">wp_url</code>,{' '}
              <code className="text-paper">featured_image_url</code>,{' '}
              <code className="text-paper">short_teaser</code>,{' '}
              <code className="text-paper">long_teaser</code>,{' '}
              <code className="text-paper">story_type</code>,{' '}
              <code className="text-paper">era</code>,{' '}
              <code className="text-paper">ai_tags</code>.
            </div>
          </Panel>

          <Panel label="Plain-text Body (optional)" accent="none">
            <textarea
              value={form.text_body}
              onChange={(e) => setForm({ ...form, text_body: e.target.value })}
              spellCheck={false}
              rows={6}
              className="w-full bg-paper-deepest border border-paper-trace px-3 py-2 font-mono text-[12px] text-paper resize-y"
            />
          </Panel>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-4">
          <Panel label="Content Selection" accent="violet" bodyClassName="p-3">
            <label className="flex items-start gap-2 cursor-pointer select-none mb-3">
              <input
                type="checkbox"
                checked={enableConfig}
                onChange={(e) => onEnableConfigChange(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="label block">Use dynamic article selection</span>
                <span className="font-mono text-[11px] text-paper-trace">
                  When on, the dispatcher will pick articles via the rules
                  below and expose them as <code className="text-paper">story_tiles</code>.
                  When off, the template renders without dynamic content
                  (use it for purely static announcements).
                </span>
              </span>
            </label>
            {enableConfig && config && (
              <ContentSelectionConfigForm
                config={config}
                onChange={setConfig}
                label="Selection Rules"
                accent="violet"
              />
            )}
          </Panel>

          <div className="font-mono text-[11px] text-paper-trace p-3 border border-ink-500/40">
            Tip: preview matches against this config on the{' '}
            <Link to="/lab/content" className="text-amber-glow hover:underline">
              Content Selector
            </Link>
            {' '}page (paste your JSON or rebuild the same filters), then run a
            full per-member preview from{' '}
            <Link to="/lab/dry-run" className="text-amber-glow hover:underline">
              Campaign Dry-Run
            </Link>
            {' '}once you've saved.
          </div>
        </div>
      </div>
    </div>
  )
}
